import { Component, Input } from '@angular/core';
import { EChartsOption, graphic } from 'echarts';
import { getGraphHeight } from '../../helpers/data-helper';
import { getBatch, isArrayWithData } from '../../../../utilities/array.helpers';
import { DashboardService } from '../../../../services/dashboard/dashboard.service';
import { AppService } from '../../../../app.service';
import { catchError, finalize, tap } from 'rxjs';
import moment from 'moment';
import { CallbackDataParams } from 'echarts/types/dist/shared';
import { SelectOptionLabelValue } from '../../../../interfaces/select-option.interface';

@Component({
    selector: 'communications-dashboard',
    templateUrl: './communications-dashboard.component.html',
    styleUrl: './communications-dashboard.component.css',
})
export class CommunicationsDashboardComponent {
    constructor(
        private dashboardService: DashboardService,
        public appService: AppService,
    ) {}

    getGraphHeight = getGraphHeight;
    isArrayWithData = isArrayWithData;
    @Input() creditors!: any[];
    @Input() timezones!: any[];
    todaysDate = new Date();

    selectedCreditors?: number[];
    fromDate: Date = new Date();
    toDate: Date = new Date();
    notificationTypes: SelectOptionLabelValue[] = [];
    selectedNotificationTypes?: number[];
    selectedTimezone?: string;
    chartOptionsBar: EChartsOption[] = [];
    chartOptionsDonut: EChartsOption | null = null;
    totalMessagesSentCommunications: number = 0;
    totalEmailsSentCommunications: number = 0;
    totalSmsSentCommunications: number = 0;
    totalVmDropsSentCommunications: number = 0;
    totalAIVoiceAgentSentCommunications: number = 0;
    totalApiDirectLinkSentCommunications: number = 0;

    ngOnInit() {
        this.getDropdownOptions();
    }

    getDropdownOptions() {
        this.dashboardService
            .getNotificationTypes()
            .pipe(
                tap((res: any) => {
                    if (isArrayWithData(res)) {
                        this.notificationTypes = res;
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.getGraphCommunications();
                }),
            )
            .subscribe();
    }

    getGraphCommunications() {
        this.appService.isShowPageSpinner = true;
        this.dashboardService
            .getGraphCommunications(
                moment(this.fromDate).format('YYYY-MM-DD'),
                moment(this.toDate).format('YYYY-MM-DD'),
                this.selectedCreditors?.map((i: any) => i.value),
                this.selectedTimezone,
                this.selectedNotificationTypes,
            )
            .pipe(
                tap((res: any) => {
                    this.generateGraphCommunicationsBar(res);
                    this.generateCommunicationsDonut(res);
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.appService.isShowPageSpinner = false;
                }),
            )
            .subscribe();
    }

    generateGraphCommunicationsBar(apiResponse: any) {
        this.chartOptionsBar = [];

        const smsAll = apiResponse.map((i: any) => i.sms);
        const emailAll = apiResponse.map((i: any) => i.email);
        const vmDropAll = apiResponse.map((i: any) => i.vmDrop);
        const aiVoiceAgentAll = apiResponse.map((i: any) => i.aiVoiceAgent);
        const apiDirectLinkAll = apiResponse.map((i: any) => i.apiDirectLink);

        const creditors = apiResponse.map((i: any) => 'creditor/' + i.id);

        const globalMax = Math.max(
            ...apiResponse.map(
                (x: any) =>
                    x.sms +
                    x.email +
                    x.vmDrop +
                    x.aiVoiceAgent +
                    x.apiDirectLink,
            ),
        );

        const TARGET_AXIS_MAX = 160_000;

        let index = 0;

        while (true) {
            const creditorBatch = getBatch(creditors, index);
            if (!isArrayWithData(creditorBatch)) break;

            const batchedSms = getBatch(smsAll, index);
            const batchedEmails = getBatch(emailAll, index);
            const batchedVmDrops = getBatch(vmDropAll, index);
            const batchedAiVoiceAgents = getBatch(aiVoiceAgentAll, index);
            const batchedApiDirectLinks = getBatch(apiDirectLinkAll, index);

            const totalChannels = getBatch(apiResponse, index).map(
                (_: any, i: number) =>
                    batchedSms[i] +
                    batchedEmails[i] +
                    batchedVmDrops[i] +
                    batchedAiVoiceAgents[i] +
                    batchedApiDirectLinks[i],
            );

            const decayFactor = Math.pow(0.75, index);
            const visualMax = globalMax / decayFactor;

            const axisDivisor =
                visualMax > TARGET_AXIS_MAX ? visualMax / TARGET_AXIS_MAX : 1;

            const isLastBatch = !isArrayWithData(
                getBatch(creditors, index + 1),
            );

            this.chartOptionsBar.push({
                dataLength: creditorBatch.length,

                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                },

                legend: index === 0 ? { top: 10, right: 20 } : undefined,

                grid: {
                    left: 130,
                    right: 120,
                    top: index === 0 ? 50 : 0,
                    bottom: 0,
                },

                xAxis: {
                    type: 'value',
                    min: 0,
                    max: visualMax,

                    axisLabel: {
                        show: isLastBatch,
                        formatter: (v: number) =>
                            Math.round(v / axisDivisor).toLocaleString(),
                    },

                    axisTick: { show: isLastBatch },
                    axisLine: { show: isLastBatch },
                    splitLine: { show: false },
                },

                yAxis: {
                    type: 'category',
                    data: creditorBatch,
                    inverse: true,
                    axisLine: { show: true },
                    axisTick: { show: false },
                },

                series: [
                    {
                        name: 'Sms',
                        type: 'bar',
                        stack: 'total',
                        barWidth: 26,
                        itemStyle: {
                            color: new graphic.LinearGradient(0, 0, 1, 0, [
                                { offset: 0, color: '#6f7b6c' },
                                { offset: 1, color: '#c7d1c5' },
                            ]),
                        },
                        data: batchedSms,
                    },
                    {
                        name: 'Email',
                        type: 'bar',
                        stack: 'total',
                        barWidth: 26,
                        itemStyle: {
                            color: new graphic.LinearGradient(0, 0, 1, 0, [
                                { offset: 0, color: '#336699' },
                                { offset: 1, color: '#b3d6f8' },
                            ]),
                        },
                        data: batchedEmails,
                    },
                    {
                        name: 'VM Drop',
                        type: 'bar',
                        stack: 'total',
                        barWidth: 26,
                        itemStyle: {
                            color: new graphic.LinearGradient(0, 0, 1, 0, [
                                { offset: 0, color: '#bd2323' },
                                { offset: 1, color: '#f88f8f' },
                            ]),
                        },
                        data: batchedVmDrops,
                    },
                    {
                        name: 'AI Voice Agent',
                        type: 'bar',
                        stack: 'total',
                        barWidth: 26,
                        itemStyle: {
                            color: new graphic.LinearGradient(0, 0, 1, 0, [
                                { offset: 0, color: '#931097' },
                                { offset: 1, color: '#f68afa' },
                            ]),
                        },
                        data: batchedAiVoiceAgents,
                    },
                    {
                        name: 'API Direct Link',
                        type: 'bar',
                        stack: 'total',
                        barWidth: 26,
                        itemStyle: {
                            color: new graphic.LinearGradient(0, 0, 1, 0, [
                                { offset: 0, color: 'rgb(255, 230, 0)' },
                                { offset: 1, color: 'rgb(252, 239, 121)' },
                            ]),
                        },
                        label: {
                            show: true,
                            position: 'right',
                            distance: 2,
                            fontWeight: 700,
                            color: '#1f2933',
                            formatter: (p: CallbackDataParams) =>
                                totalChannels[p.dataIndex!]?.toLocaleString(),
                        },
                        data: batchedApiDirectLinks,
                    },
                ],
            });

            index++;
        }

        // totals
        this.totalSmsSentCommunications = smsAll.reduce(
            (s: number, x: number) => s + x,
            0,
        );
        this.totalEmailsSentCommunications = emailAll.reduce(
            (s: number, x: number) => s + x,
            0,
        );
        this.totalVmDropsSentCommunications = vmDropAll.reduce(
            (s: number, x: number) => s + x,
            0,
        );
        this.totalAIVoiceAgentSentCommunications = aiVoiceAgentAll.reduce(
            (s: number, x: number) => s + x,
            0,
        );
        this.totalApiDirectLinkSentCommunications = apiDirectLinkAll.reduce(
            (s: number, x: number) => s + x,
            0,
        );
    }

    generateCommunicationsDonut(apiResponse: any) {
        const dataCommunicationsShare = apiResponse.map((i: any) => ({
            name: 'creditor/' + i.id,
            value:
                i.sms + i.email + i.vmDrop + i.aiVoiceAgent + i.apiDirectLink,
            color: i.color,
        }));

        const totalCommunicationsShare = dataCommunicationsShare.reduce(
            (s: number, x: any) => s + x.value,
            0,
        );

        this.chartOptionsDonut = {
            legend: {
                orient: 'vertical',
                right: 20,
                top: 80,
                bottom: 20,
                icon: 'circle',
                itemWidth: 14,
                itemHeight: 14,
                textStyle: { fontSize: 13 },
            },

            tooltip: {
                trigger: 'item',
                formatter: (p: any) =>
                    `<b>${p.name}</b><br/>${p.value.toLocaleString()} (${p.percent}%)`,
            },

            series: [
                {
                    type: 'pie',
                    radius: ['58%', '78%'],
                    center: ['38%', '55%'],
                    avoidLabelOverlap: true,
                    minAngle: 3,
                    labelLine: { length: 14, length2: 8 },
                    label: {
                        formatter: (p: any) => {
                            // optional: hide tiny slices
                            if (p.percent < 1) return '';

                            const value =
                                p.value >= 1000
                                    ? `${(p.value / 1000).toFixed(1)}K`
                                    : p.value.toString();

                            return `{p|${p.percent}%}\n{v|${value}}`;
                        },
                        rich: {
                            p: { fontSize: 12, color: '#64748b' },
                            v: { fontSize: 14, fontWeight: 600 },
                        },
                    },

                    data: dataCommunicationsShare.map((x: any) => ({
                        value: x.value,
                        name: x.name,
                        itemStyle: { color: x.color },
                    })),
                },
            ],

            graphic: [
                {
                    type: 'text',
                    left: '38%',
                    top: '55%',
                    style: {
                        text: totalCommunicationsShare.toLocaleString(),
                        fontSize: 26,
                        fontWeight: 700,
                        fill: '#111',
                    },
                },
                {
                    type: 'text',
                    left: '38%',
                    top: '63%',
                    style: {
                        text: 'Total',
                        fontSize: 13,
                        fill: '#64748b',
                    },
                },
            ],
        };
    }

    setupGraphCommunicationsMock() {
        const dataCommunicationsShare = [
            { name: 'creditor/105', value: 75000, color: '#6f7b6c' },
            { name: 'creditor/55', value: 220000, color: '#2f5f8f' },
            { name: 'creditor/15', value: 120000, color: '#c7b3ef' },
            { name: 'creditor/115', value: 90000, color: '#eb6a67' },
            { name: 'Others', value: 34000, color: '#c9d9c5' },
        ];

        const totalCommunicationsShare = dataCommunicationsShare.reduce(
            (s: any, x: any) => s + x.value,
            0,
        );

        this.chartOptionsDonut = {
            legend: {
                orient: 'vertical',
                right: 20,
                top: 80,
                icon: 'circle',
                itemWidth: 14,
                itemHeight: 14,
                textStyle: { fontSize: 13 },
            },
            tooltip: {
                trigger: 'item',
                formatter: (p: any) =>
                    `<b>${p.name}</b><br/>${p.value.toLocaleString()} (${
                        p.percent
                    }%)`,
            },
            series: [
                {
                    type: 'pie',
                    radius: ['58%', '78%'],
                    center: ['38%', '55%'],
                    avoidLabelOverlap: false,
                    labelLine: { length: 14, length2: 8 },
                    label: {
                        formatter: (p: any) =>
                            `{p|${p.percent}%}\n{v|${(p.value / 1000).toFixed(
                                1,
                            )}K}`,
                        rich: {
                            p: { fontSize: 12, color: '#64748b' },
                            v: { fontSize: 14, fontWeight: 600 },
                        },
                    },
                    data: dataCommunicationsShare.map((x: any) => ({
                        value: x.value,
                        name: x.name,
                        itemStyle: { color: x.color },
                    })),
                },
            ],

            graphic: [
                {
                    type: 'text',
                    left: '38%',
                    top: '55%',
                    style: {
                        text: totalCommunicationsShare.toLocaleString(),
                        fontSize: 26,
                        fontWeight: 700,
                        fill: '#111',
                    },
                },
                {
                    type: 'text',
                    left: '38%',
                    top: '63%',
                    style: {
                        text: 'Total',
                        fontSize: 13,
                        fill: '#64748b',
                    },
                },
            ],
        };
    }

    onGenerateCommunications() {}
    onChangeCreditors(event: any) {}
    onChangeNotificationTypes(event: any) {}
}
